import { useCallback, useEffect, useRef, useState } from "react";
import { message, Upload } from "antd";
import type { CropViewportState } from "../Utils/imageCrop/cropImageFile";
import { resolveCropViewportStateForEdit } from "../Utils/imageCrop/resolveCropViewportStateForEdit";

type CropSessionSnapshot = {
  originalSourceFile: File | null;
  originalSourceUrl: string | null;
};

type OpenCropperOptions = {
  restoreCrop?: boolean;
  cropState?: CropViewportState | null;
};

type EditOriginalImageOptions = {
  croppedImageUrl?: string;
  /** Fallback when originalSourceUrl state may not be set yet. */
  sourceUrl?: string;
  /** Persisted zoom/pan — preferred over image-diff derive when present. */
  cropState?: CropViewportState | null;
  /** Per-open override of hook `defaultFit` (e.g. contain for display-only URLs). */
  defaultFit?: "contain" | "cover";
};

type UseImageCropperOptions = {
  /** Aspect ratio used when reconstructing crop from original + cropped URLs. */
  aspectRatio?: number;
  /** Must match ImageCropPreset viewport when fixed (e.g. product bank 143×250). */
  viewportWidth?: number;
  viewportHeight?: number;
  /**
   * Edit-restore only (editOriginalImage): after JSON + image-diff fail,
   * match CSS cover+center. New uploads via openCropperForFile stay contain.
   */
  defaultFit?: "contain" | "cover";
};

export function useImageCropper(options?: UseImageCropperOptions) {
  const aspectRatio = options?.aspectRatio;
  const viewportWidth = options?.viewportWidth;
  const viewportHeight = options?.viewportHeight;
  const defaultFit = options?.defaultFit;
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState<string | null>(null);
  const [originalSourceFile, setOriginalSourceFile] = useState<File | null>(null);
  const [originalSourceUrl, setOriginalSourceUrlState] = useState<string | null>(
    null
  );
  const [savedCropState, setSavedCropState] = useState<CropViewportState | null>(
    null
  );
  const [initialCropState, setInitialCropState] = useState<CropViewportState | null>(
    null
  );
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);
  const cropperObjectUrlRef = useRef<string | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const cropSessionSnapshotRef = useRef<CropSessionSnapshot | null>(null);
  const originalSourceUrlRef = useRef<string | null>(null);

  const setOriginalSourceUrlValue = useCallback((url: string | null) => {
    originalSourceUrlRef.current = url;
    setOriginalSourceUrlState(url);
  }, []);

  const closeCropper = useCallback(() => {
    if (cropperObjectUrlRef.current) {
      URL.revokeObjectURL(cropperObjectUrlRef.current);
      cropperObjectUrlRef.current = null;
    }
    setCropperOpen(false);
    setCropperImageSrc(null);
    setInitialCropState(null);
  }, []);

  const beginCropSession = useCallback(() => {
    cropSessionSnapshotRef.current = {
      originalSourceFile,
      originalSourceUrl,
    };
  }, [originalSourceFile, originalSourceUrl]);

  const commitCropSession = useCallback(() => {
    cropSessionSnapshotRef.current = null;
  }, []);

  const cancelCropper = useCallback(() => {
    const snapshot = cropSessionSnapshotRef.current;
    closeCropper();
    if (snapshot) {
      setOriginalSourceFile(snapshot.originalSourceFile);
      setOriginalSourceUrlValue(snapshot.originalSourceUrl);
      cropSessionSnapshotRef.current = null;
    }
  }, [closeCropper, setOriginalSourceUrlValue]);

  const clearOriginalSource = useCallback(() => {
    setOriginalSourceFile(null);
    setOriginalSourceUrlValue(null);
    setSavedCropState(null);
    setInitialCropState(null);
  }, [setOriginalSourceUrlValue]);

  const clearSessionOriginalFile = useCallback(() => {
    setOriginalSourceFile(null);
  }, []);

  const updateOriginalSourceUrl = useCallback(
    (url: string | null) => {
      const next = url ?? null;
      const prev = originalSourceUrlRef.current;
      if (String(prev ?? "").trim() === String(next ?? "").trim()) {
        return;
      }
      setOriginalSourceUrlValue(next);
      setSavedCropState(null);
      setInitialCropState(null);
    },
    [setOriginalSourceUrlValue]
  );

  const recordCropViewportState = useCallback((cropState: CropViewportState) => {
    setSavedCropState(cropState);
  }, []);

  useEffect(
    () => () => {
      if (cropperObjectUrlRef.current) {
        URL.revokeObjectURL(cropperObjectUrlRef.current);
      }
    },
    []
  );

  const resolveCropStateForRestore = useCallback(
    async (
      options?: OpenCropperOptions & EditOriginalImageOptions
    ): Promise<CropViewportState | null> => {
      const croppedUrl = String(options?.croppedImageUrl ?? "").trim();
      const originalUrl = String(
        options?.sourceUrl ?? originalSourceUrl ?? ""
      ).trim();

      // `cropState: null` must win over session state (`??` would ignore null).
      const cropState =
        options != null && "cropState" in options
          ? options.cropState ?? null
          : savedCropState;

      return resolveCropViewportStateForEdit({
        cropState,
        originalUrl,
        croppedUrl,
        aspectRatio,
        viewport:
          viewportWidth && viewportHeight
            ? { width: viewportWidth, height: viewportHeight }
            : null,
        defaultFit: options?.defaultFit ?? defaultFit,
      });
    },
    [
      aspectRatio,
      defaultFit,
      originalSourceUrl,
      savedCropState,
      viewportHeight,
      viewportWidth,
    ]
  );

  const openCropperForFile = useCallback(
    (file: File, options?: OpenCropperOptions) => {
      beginCropSession();
      setOriginalSourceFile(file);

      if (!options?.restoreCrop) {
        // Fresh upload — keep contain @ zoom 1 (do not apply cover fallback).
        setSavedCropState(null);
        setInitialCropState(null);
      } else {
        const stateToRestore = options.cropState ?? savedCropState;
        if (options.cropState) {
          setSavedCropState(options.cropState);
        }
        setInitialCropState(stateToRestore);
      }

      if (cropperObjectUrlRef.current) {
        URL.revokeObjectURL(cropperObjectUrlRef.current);
      }
      const url = URL.createObjectURL(file);
      cropperObjectUrlRef.current = url;
      setCropperImageSrc(url);
      setCropperOpen(true);
    },
    [beginCropSession, savedCropState]
  );

  const openCropperFromUrl = useCallback(
    async (url: string, options?: OpenCropperOptions) => {
      if (!url) {
        message.warning("No original image available to edit.");
        return;
      }

      const openWithRemoteUrl = () => {
        beginCropSession();

        if (!options?.restoreCrop) {
          setSavedCropState(null);
          setInitialCropState(null);
        } else {
          const stateToRestore = options.cropState ?? savedCropState;
          if (options.cropState) {
            setSavedCropState(options.cropState);
          }
          setInitialCropState(stateToRestore);
        }

        if (cropperObjectUrlRef.current) {
          URL.revokeObjectURL(cropperObjectUrlRef.current);
          cropperObjectUrlRef.current = null;
        }

        setOriginalSourceUrlValue(url);
        setCropperImageSrc(url);
        setCropperOpen(true);
      };

      try {
        setIsLoadingOriginal(true);
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) {
          throw new Error("Failed to fetch original image");
        }

        const blob = await response.blob();
        const file = new File(
          [blob],
          `original-image-${Date.now()}.jpg`,
          { type: blob.type || "image/jpeg" }
        );
        openCropperForFile(file, options);
      } catch {
        // Mobile Safari / CORS often blocks blob fetch for storage URLs.
        // Still open the cropper with the remote URL so Edit Image works.
        openWithRemoteUrl();
      } finally {
        setIsLoadingOriginal(false);
      }
    },
    [
      beginCropSession,
      openCropperForFile,
      savedCropState,
      setOriginalSourceUrlValue,
    ]
  );

  const canEditOriginal = Boolean(originalSourceFile || originalSourceUrl);

  const editOriginalImage = useCallback(
    async (options?: EditOriginalImageOptions) => {
      try {
        setIsLoadingOriginal(true);
        const fallbackSource = String(options?.sourceUrl ?? "").trim() || null;
        // Prefer caller source (true original). Use Value setter so we do not
        // clear saved crop state the way updateOriginalSourceUrl does.
        if (fallbackSource) {
          setOriginalSourceUrlValue(fallbackSource);
        }

        const cropState = await resolveCropStateForRestore({
          restoreCrop: true,
          croppedImageUrl: options?.croppedImageUrl,
          sourceUrl: fallbackSource ?? undefined,
          defaultFit: options?.defaultFit,
          // Only forward when the caller set the key — so `null` can mean
          // "no crop" without wiping session restore for callers that omit it.
          ...(options != null && "cropState" in options
            ? { cropState: options.cropState ?? null }
            : {}),
        });
        const openOptions: OpenCropperOptions = {
          restoreCrop: true,
          cropState,
        };

        if (originalSourceFile) {
          openCropperForFile(originalSourceFile, openOptions);
          return;
        }

        const urlToOpen = fallbackSource || originalSourceUrl;
        if (urlToOpen) {
          await openCropperFromUrl(urlToOpen, openOptions);
          return;
        }

        message.warning("No original image available to edit.");
      } finally {
        setIsLoadingOriginal(false);
      }
    },
    [
      openCropperForFile,
      openCropperFromUrl,
      originalSourceFile,
      originalSourceUrl,
      resolveCropStateForRestore,
      setOriginalSourceUrlValue,
    ]
  );

  const beforeUploadImage = useCallback(
    (file: File) => {
      if (!file.type.startsWith("image/")) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      openCropperForFile(file);
      return false;
    },
    [openCropperForFile]
  );

  const handleCropReplace = useCallback(() => {
    replaceInputRef.current?.click();
  }, []);

  return {
    cropperOpen,
    cropperImageSrc,
    initialCropState,
    originalSourceFile,
    originalSourceUrl,
    canEditOriginal,
    isLoadingOriginal,
    closeCropper,
    cancelCropper,
    commitCropSession,
    clearOriginalSource,
    clearSessionOriginalFile,
    setOriginalSourceUrl: updateOriginalSourceUrl,
    recordCropViewportState,
    openCropperForFile,
    editOriginalImage,
    beforeUploadImage,
    handleCropReplace,
    replaceInputRef,
  };
}
